import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './Docs.css';

export default function Docs() {
  return (
    <div className="docs-lite">
      <Helmet>
        <link rel="canonical" href="https://www.joegalaxy.net/docs" />
      </Helmet>

      <header className="docs-lite__header">
        <h1>Presence Engine Documentation</h1>
      </header>

      <main className="docs-lite__content">
        <p>
          This documentation explains <strong>lifecycle-aware presence</strong> and{' '}
          <strong>presence-native coordination</strong> for real-time systems.
        </p>
        <p>
          Presence is modeled as a <strong>time-varying signal</strong>, not a binary online/offline
          flag.
          <br />
          These documents describe the mental models, patterns, and architectural decisions
          required to build presence you can trust.
        </p>
        <p>
          To see this model running live, visit the{' '}
          <a href="https://www.joegalaxy.net/presence-demo">presence demo</a>.
        </p>

        <hr />

        <section>
          <h2>Core concepts</h2>
          <p>
            These pages define the canonical presence model and its most common failure modes.
          </p>
          <ul>
            <li>
              <Link to="/docs/presence-online-offline.md">Online vs Offline Presence</Link>
              <br />
              Why binary presence fails and how lifecycle-aware presence models real user behavior.
            </li>
            <li>
              <Link to="/docs/whos-online-api.md">Who’s Online API</Link>
              <br />
              Designing a reliable “who’s online” API by separating sessions from users.
            </li>
            <li>
              <Link to="/docs/presence-socket-io.md">Presence with Socket.IO</Link>
              <br />
              Transport edge cases, reconnect behavior, and why Socket.IO alone is insufficient.
            </li>
            <li>
              <Link to="/docs/webrtc-presence.md">WebRTC Presence</Link>
              <br />
              Why real-time calls require presence, readiness, and intent before media starts.
            </li>
          </ul>
        </section>

        <hr />

        <section>
          <h2>Patterns</h2>
          <p>
            These documents cover focused implementation patterns that support the core model.
          </p>
          <ul>
            <li>
              <Link to="/docs/patterns/multi-tab.md">Multi-tab Presence</Link>
              <br />
              Handling multiple tabs and devices without user-level flapping.
            </li>
            <li>
              <Link to="/docs/patterns/idle-detection.md">Idle Detection</Link>
              <br />
              Distinguishing connected sessions from active humans.
            </li>
            <li>
              <Link to="/docs/patterns/reconnects.md">Reconnect Handling</Link>
              <br />
              Grace windows, transient disconnects, and network jitter.
            </li>
            <li>
              <Link to="/docs/patterns/session-vs-user.md">Sessions vs Users</Link>
              <br />
              Why presence must be session-first and user-derived.
            </li>
          </ul>
        </section>

        <hr />

        <section>
          <h2>How to read these docs</h2>
          <p>
            Start with <strong>Online vs Offline Presence</strong>, then read{' '}
            <strong>Who’s Online API</strong> and <strong>Presence with Socket.IO</strong>.
          </p>
          <p>These establish the core model.</p>
          <p>From there:</p>
          <ul>
            <li>
              Use <strong>WebRTC Presence</strong> to understand coordination and A/V readiness
            </li>
            <li>
              Reference <strong>Patterns</strong> for specific edge cases and implementation
              guidance
            </li>
          </ul>
        </section>

        <hr />

        <section>
          <h2>Relationship to JG Presence Engine</h2>
          <p>
            JG Presence Engine implements these models in production as part of{' '}
            <strong>JG Universe</strong>.
          </p>
          <p>
            This documentation is published as a <strong>canonical reference</strong> for
            lifecycle-aware presence and presence-native coordination.
            <br />
            It intentionally focuses on <em>architecture and patterns</em>, not SDK internals.
          </p>
        </section>

        <hr />

        <section>
          <h2>Philosophy</h2>
          <p>
            JG Presence Engine does not impose a presence model.
            <br />
            It exposes presence as a signal you can shape.
          </p>
        </section>

        <p className="docs-lite__source">
          —
          <br />
          See this model running live:
          <br />
          <a href="https://www.joegalaxy.net/presence-demo">
            https://www.joegalaxy.net/presence-demo
          </a>
          <br />
          <a href="https://github.com/thejoegalaxy/presence-engine/blob/main/docs/index.md">
            View source on GitHub
          </a>
        </p>
      </main>
    </div>
  );
}
